import { ResponseError } from "@/api-clients/fetch-config";
import { FAILED_NETWORK_HTTP_STATUS } from "@/data/response-utils";
import { Ban, CloudAlert, CloudOff, SearchX } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { responseErrorAlertTestIds } from "@/lib/data-test-ids";

const getErrorConfig = (status: number | null) => {
  switch (status) {
    case 500:
      return {
        icon: <CloudAlert className="size-4 shrink-0" />,
        title: "Something went wrong",
        description: (
          <p>
            We&apos;re having trouble processing your request right now. Please
            try again in a moment.
          </p>
        ),
      };
    case 404:
      return {
        icon: <SearchX className="size-4 shrink-0" />,
        title: "Not Found",
        description: (
          <p>The content you are looking for is not available anymore.</p>
        ),
      };
    case 403:
      return {
        icon: <Ban className="size-4 shrink-0" />,
        title: "Forbidden Access",
        description: (
          <p>You don&apos;t have permission to access this resource.</p>
        ),
      };
    case FAILED_NETWORK_HTTP_STATUS:
      return {
        icon: <CloudOff className="size-4 shrink-0" />,
        title: "Network Error",
        description: (
          <p>
            There was a problem communicating with the server. Please check your
            connection and try again.
          </p>
        ),
      };
    default:
      return {
        icon: <CloudAlert className="size-4 shrink-0" />,
        title: "Unexpected Error",
        description: <p>An unexpected issue occurred. Please try again.</p>,
      };
  }
};

export function ResponseErrorAlert({ error }: { error?: unknown }) {
  const status = error instanceof ResponseError ? error.status : null;
  const { icon, title, description } = getErrorConfig(status);

  return (
    <Alert
      variant="default"
      className="border-border/50 bg-muted/30"
      data-testid={responseErrorAlertTestIds.CONTAINER_ELEMENT}
    >
      {icon}
      <AlertTitle className="line-clamp-none text-sm font-semibold">
        {title}
      </AlertTitle>
      <AlertDescription className="text-sm text-muted-foreground leading-relaxed">
        {description}
      </AlertDescription>
    </Alert>
  );
}
